import { createContext, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIsWide } from '@/lib/use-media-query';

/**
 * Die Hauptaktion einer Seite steht im Entwurf *in der Kopfzeile* – neben der
 * Suche, nicht unter der Überschrift. „Person anlegen", „Bilder hochladen",
 * „Abfotografieren": auf jeder Albumseite eine Handbewegung, immer an
 * derselben Stelle.
 *
 * Der Weg dorthin ist ein Portal und kein Zustand, den die Seiten in die
 * Kopfzeile hochmelden. Der Unterschied ist nicht Geschmack:
 *
 *   - Ein Portal rendert die Knöpfe *dort, wo sie im Baum stehen* – mit dem
 *     Zustand und den Handlern der Seite. Über einen gemeldeten Zustand müsste
 *     jede Seite ihre Handler in die Kopfzeile hineinreichen und beim
 *     Verlassen wieder abmelden; das ist die Sorte Buchführung, die genau
 *     einmal vergessen wird und dann einen toten Knopf hinterlässt.
 *   - Beim Seitenwechsel verschwinden die Knöpfe von selbst, weil die Seite
 *     verschwindet.
 *
 * Auf schmalen Bildschirmen gibt es keine Kopfzeile mit Platz – dort rendern
 * dieselben Knöpfe an ihrer Stelle auf der Seite. Deshalb `useIsWide` und
 * nicht `hidden lg:flex`: sonst stünden sie zweimal im Baum, mit zwei
 * Zuständen und zwei Vorleseansagen.
 */
const HeaderSlotContext = createContext<HTMLElement | null>(null);

export function HeaderSlotProvider({
  children,
  slot,
}: {
  children: React.ReactNode;
  slot: HTMLElement | null;
}) {
  return <HeaderSlotContext.Provider value={slot}>{children}</HeaderSlotContext.Provider>;
}

/**
 * Der Platzhalter in der Kopfzeile. Gibt das Element weiter, in das die Seiten
 * hineinrendern – über eine Rückruf-Referenz, damit das Portal beim ersten
 * Anhängen schon ein Ziel hat.
 */
export function useHeaderSlot(): [React.RefCallback<HTMLDivElement>, HTMLDivElement | null] {
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  return [setSlot, slot];
}

/**
 * Die Aktionen einer Seite. Breit: in der Kopfzeile. Schmal: an dieser Stelle.
 */
export function HeaderActions({ children }: { children: React.ReactNode }) {
  const slot = useContext(HeaderSlotContext);
  const wide = useIsWide();

  if (wide && slot) {
    return createPortal(<div className="flex items-center gap-2">{children}</div>, slot);
  }
  return <div className="flex flex-wrap items-center gap-2">{children}</div>;
}

// ── Kopfzeile abschalten ────────────────────────────────────────────────────

const HideHeaderContext = createContext<((hidden: boolean) => void) | null>(null);

export function HideHeaderProvider({
  children,
  onChange,
}: {
  children: React.ReactNode;
  onChange: (hidden: boolean) => void;
}) {
  return <HideHeaderContext.Provider value={onChange}>{children}</HideHeaderContext.Provider>;
}

/**
 * Diese Seite kommt ohne die Kopfzeile der App aus.
 *
 * Gedacht für die Bildseite: 2d zeigt dort die schmale Symbolleiste und
 * darüber *ein* Kopfband – das der Seite, mit „Zurück ins Fotoalbum", Titel
 * und Blättern. Zwei Kopfzeilen übereinander nehmen dem Bild 140px Höhe und
 * stellen zwei Suchfelder-und-Zurück-Zeilen nebeneinander, die nichts
 * miteinander zu tun haben.
 *
 * Schmal ist ohnehin keine da, seit die Kopfzeile der App nur noch ab `lg`
 * steht (siehe `app-shell.tsx`) – dort geht dieser Wunsch ins Leere und muss
 * es auch: Suche und Konto liegen jetzt auf der Startseite und in der
 * „Mehr“-Schublade, nicht über jeder Seite.
 */
export function useHideAppHeader() {
  const onChange = useContext(HideHeaderContext);

  useEffect(() => {
    if (!onChange) return;
    onChange(true);
    return () => onChange(false);
  }, [onChange]);
}

// ── Navigationsschublade ────────────────────────────────────────────────────

interface NavDrawer {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const NavDrawerContext = createContext<NavDrawer | null>(null);

export function NavDrawerProvider({
  children,
  value,
}: {
  children: React.ReactNode;
  value: NavDrawer;
}) {
  return <NavDrawerContext.Provider value={value}>{children}</NavDrawerContext.Provider>;
}

/**
 * Die Schublade mit allen Bereichen – auf, zu, und ob sie offen steht.
 *
 * Sie hängt am „Mehr“-Feld der Leiste unten: Stammbaum, Orte, Ereignisse,
 * Quellen, Lücken und die Verwaltung passen nicht in fünf Felder. Dass der
 * Zustand mit hierher kommt und nicht nur der Öffner, hat einen sichtbaren
 * Grund: „Mehr“ steht hervorgehoben da, solange seine Schublade offen ist –
 * sonst wäre es das einzige Feld der Leiste ohne jede Rückmeldung.
 */
export function useNavDrawer(): NavDrawer | null {
  return useContext(NavDrawerContext);
}
